import { readPersistedStore } from '../store/persistentStore';

const LOCK_NAME = 'pepper-business-token-refresh';

/**
 * @description The token in the persisted store, or null. Read from
 * localStorage rather than Vuex because another tab's refresh writes there,
 * while this tab's in-memory copy still holds the token it started with.
 * @returns {{access_token: string, refresh_token: string}|null}
 */
const persistedToken = () => readPersistedStore().auth?.token ?? null;

/**
 * @description Run `refresh` under a lock shared by every tab of this origin, so
 * at most one tab is ever mid-refresh — otherwise two tabs spend the same
 * single-use refresh_token and the loser is force-logged-out. `onAlreadyRefreshed`
 * runs instead when another tab rotated the token while this one waited for the
 * lock. Holding the lock for the whole refresh is what lets callers assume no
 * other tab can rotate mid-flight. Both are plain thunks: whichever runs, the
 * caller sees one promise that settles when the token is usable.
 * @param requestToken the access_token this refresh attempt was triggered by
 * @param refresh () => Promise
 * @param onAlreadyRefreshed (token) => Promise
 * @returns {Promise}
 */
export const withCrossTabRefreshLock = (requestToken, refresh, onAlreadyRefreshed) => {
  const runInsideLock = () => {
    const current = persistedToken();

    if (current?.access_token && current.access_token !== requestToken) {
      return onAlreadyRefreshed(current);
    }

    return refresh();
  };

  if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
    return runInsideLock();
  }

  return navigator.locks.request(LOCK_NAME, runInsideLock);
};
